import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatDateDisplay } from "../../lib/utils";
import { Pencil } from "lucide-react";
import AdminOrderPopup from "./AdminOrderPopup";
import AdminOrderEditPopup from "./AdminOrderEditPopup";

const STATUS_COLORS = {
  placed:     "bg-gold/20 text-gold",
  processing: "bg-sky-500/20 text-sky-300",
  shipped:    "bg-violet-500/20 text-violet-300",
  delivered:  "bg-emerald-500/20 text-emerald-300",
  cancelled:  "bg-rose-500/20 text-rose-300",
};

const fmt = (v) => (v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/orders");
    setOrders(r.data);
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Sales</div>
      <h1 className="text-3xl font-black uppercase tracking-tighter mb-8">Orders</h1>

      <div className="bg-navy-700 rounded-xl neon-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
            <tr>
              <th className="text-left p-4">Order ID</th>
              <th className="text-left p-4">Customer</th>
              <th className="text-left p-4">Phone</th>
              <th className="text-right p-4">Total</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Payment</th>
              <th className="text-left p-4">Date</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr
                key={o.id}
                onClick={() => setActive(o)}
                className={`border-t border-gold/10 hover:bg-navy-800/50 cursor-pointer ${o.status === "cancelled" ? "opacity-50" : ""}`}
              >
                <td className="p-4 font-mono text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    {o.order_number}
                    {o.order_type === "preorder" && (
                      <span className="bg-gold/20 text-gold text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded">PRE-ORDER</span>
                    )}
                    {o.tracking_link && (
                      <span className="text-violet-400 text-[9px] uppercase tracking-wider">• tracked</span>
                    )}
                    {o.coupon_code && (
                      <span className="text-emerald-400 text-[9px] font-mono uppercase tracking-wider">• {o.coupon_code}</span>
                    )}
                  </div>
                  {o.order_type === "preorder" && o.release_date && (
                    <div className="text-[10px] text-gold/60 mt-0.5">Releases {formatDateDisplay(o.release_date)}</div>
                  )}
                </td>
                <td className="p-4">
                  <div className="font-semibold">{o.customer_name}</div>
                  {o.customer_email && <div className="text-xs text-slate-400">{o.customer_email}</div>}
                </td>
                <td className="p-4 text-slate-300">{o.customer_phone || "—"}</td>
                <td className="p-4 text-right font-bold text-gold">₹{fmt(o.total)}</td>
                <td className="p-4">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${STATUS_COLORS[o.status] || "bg-slate-700 text-slate-300"}`}>
                    {o.status}
                  </span>
                </td>
                <td className="p-4 uppercase text-xs">{o.payment_method}</td>
                <td className="p-4 text-xs text-slate-400">{formatDateDisplay(o.created_at)}</td>
                <td className="p-4">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditing(o); }}
                    title="Edit order details"
                    className="p-1.5 text-slate-400 hover:text-gold transition"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">No orders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {active && (
        <AdminOrderPopup
          order={active}
          onClose={() => setActive(null)}
          onUpdated={load}
        />
      )}

      {editing && (
        <AdminOrderEditPopup
          order={editing}
          onClose={() => setEditing(null)}
          onUpdated={load}
        />
      )}
    </div>
  );
}
